import {
  createContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { motion } from 'motion/react';
import LogoText from '../../components/LogoText';
import StarIcon from '../../components/StarIcon';
import sleep from '../../util/sleep';
import { uniqueId } from 'lodash';

let STARS = [
  { size: 58, style: { right: '10%', top: '35%' } },
  { size: 38, style: { right: '22%', top: '30%' } },
  { size: 24, style: { right: '10%', top: '28%' } },
  { size: 24, style: { left: '10%', bottom: '32%' } },
  { size: 58, style: { left: '17%', bottom: '24%' } },
];

let ANIMATIONS = [
  {
    leadIn: 800,
    starIndex: 4,
    scaleTo: 1.5,
  },
  {
    leadIn: 100,
    starIndex: 3,
    scaleTo: 1.65,
  },
  {
    leadIn: 500,
    starIndex: 0,
    scaleTo: 1.4,
  },
  {
    leadIn: 100,
    starIndex: 1,
    scaleTo: 1.3,
  },
  {
    leadIn: 0,
    starIndex: 2,
    scaleTo: 1.72,
  },
];

let SplashLoaderPersistentRender = () => {
  let [twinkle, setTwinkle] = useState<{
    starIndex: number;
    scaleTo: number;
  } | null>(null);

  let cancelTwinkleRef = useRef(false);

  let runTwinkle = useCallback(async () => {
    let runIndex = 0;
    await sleep(300);
    while (runIndex < ANIMATIONS.length) {
      let { leadIn, scaleTo, starIndex } = ANIMATIONS[runIndex];
      await sleep(leadIn);
      if (cancelTwinkleRef.current) return;
      setTwinkle({ starIndex, scaleTo });
      await sleep(200);
      if (cancelTwinkleRef.current) return;
      setTwinkle(null);
      runIndex++;
      if (runIndex === ANIMATIONS.length) {
        await sleep(2400);
        runIndex = 0;
      }
    }
  }, []);

  useEffect(() => {
    cancelTwinkleRef.current = false;
    runTwinkle();
    return () => {
      cancelTwinkleRef.current = true;
    };
  }, [runTwinkle]);

  return (
    <div className="absolute inset-0 z-50 overflow-auto bg-orange-default p-5 text-neutral-100">
      <div className="relative mx-auto h-full w-full max-w-[360px]">
        <LogoText className="relative top-1/2 w-full -translate-y-1/2" />
        {STARS.map((star, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={star.style}
            initial={{ scale: 0 }}
            animate={{
              scale: twinkle?.starIndex === index ? twinkle.scaleTo : 1,
            }}
            transition={{ type: 'spring', stiffness: 200, damping: 24 }}
          >
            <StarIcon style={{ width: star.size, height: star.size }} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export let SplashLoaderContext = createContext({
  register: () => {
    return () => {};
  },
});

let SplashLoaderRoot = ({ children }: { children: React.ReactNode }) => {
  let [splashLoaderRegistrees, setSplashLoaderRegistrees] = useState<string[]>(
    [],
  );
  let [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (splashLoaderRegistrees.length > 0) {
      setIsVisible(true);
    } else {
      let timeout = setTimeout(() => setIsVisible(false), 220);
      return () => clearTimeout(timeout);
    }
  }, [splashLoaderRegistrees.length]);

  let register = useCallback(() => {
    let id = uniqueId();
    setSplashLoaderRegistrees((last) => [...last, id]);
    return () => {
      setSplashLoaderRegistrees((last) => last.filter((item) => item !== id));
    };
  }, [setSplashLoaderRegistrees]);

  let value = useMemo(() => ({ register }), [register]);

  return (
    <SplashLoaderContext.Provider value={value}>
      {isVisible && <SplashLoaderPersistentRender />}
      {children}
    </SplashLoaderContext.Provider>
  );
};

export default SplashLoaderRoot;
